import { createContext, useContext, useEffect, useState } from 'react'
import { apiFetch, ApiError } from '../lib/apiClient'
import type { UserProfile } from '../types/profile'
import { trackAuthEvent, setTelemetryAccount } from '../lib/telemetry'
import { applyBranding, PRAGMELIS_BRANDING } from '../lib/branding'

interface ProfileState {
  profile: UserProfile | null
  isLoading: boolean
  error: ApiError | null
}

export const ProfileContext = createContext<ProfileState>({
  profile: null,
  isLoading: true,
  error: null,
})

export function useProfile(): ProfileState {
  return useContext(ProfileContext)
}

export function useProfileLoader(enabled = true): ProfileState {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setIsLoading(true)
    apiFetch<UserProfile>('/api/profile')
      .then(p => {
        if (!cancelled) {
          setProfile(p)
          applyBranding(p.branding ?? PRAGMELIS_BRANDING)
          // Spec 081 — corrélation RUM : accountId interne (GUID), jamais de PII
          setTelemetryAccount(p.userAccountId)
          trackAuthEvent('auth_login_success', {
            accountId: p.accounts[0]?.accountId ?? 'unknown',
            userId: p.userId,
            role: p.role,
          })
        }
      })
      .catch(err => { if (!cancelled) setError(err instanceof ApiError ? err : new ApiError(500, 'UNKNOWN')) })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [enabled])

  return { profile, isLoading, error }
}
